import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { isAuthenticated, fetchMe, logout } from "@/services/auth.service";
import type { AuthUser } from "@/services/auth.service";

export interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  refresh: () => Promise<void>;
  signOut: () => void;
}

export function useRequireAuth(): AuthContextValue {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    try {
      const me = await fetchMe();
      setUser(me);
    } catch {
      logout();
      setUser(null);
      navigate(`/admin/login?redirect=${encodeURIComponent(location.pathname)}`, { replace: true });
    }
  }

  useEffect(() => {
    if (!isAuthenticated()) {
      setLoading(false);
      navigate(`/admin/login?redirect=${encodeURIComponent(location.pathname)}`, { replace: true });
      return;
    }
    let active = true;
    fetchMe()
      .then((me) => {
        if (active) setUser(me);
      })
      .catch(() => {
        logout();
        if (active) {
          setUser(null);
          navigate(`/admin/login?redirect=${encodeURIComponent(location.pathname)}`, { replace: true });
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [navigate, location.pathname]);

  function signOut() {
    logout();
    setUser(null);
    navigate("/admin/login", { replace: true });
  }

  return { user, loading, refresh, signOut };
}